import { useState, useEffect, useCallback } from 'react'

/**
 * Custom hook for auto-refreshing data and managing loading states
 * @param {Function} fetchFunction - Function to fetch data
 * @param {number} interval - Refresh interval in milliseconds (default: 5 minutes)
 * @param {boolean} enableAutoRefresh - Whether to enable auto-refresh
 * @returns {Object} - { data, loading, error, refresh, lastUpdated }
 */
export const useAutoRefresh = (
  fetchFunction, 
  interval = 5 * 60 * 1000, // 5 minutes default
  enableAutoRefresh = true
) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [lastUpdated, setLastUpdated] = useState(null)

  const refresh = useCallback(async () => {
    if (!fetchFunction) return

    setLoading(true)
    setError(null)
    
    try {
      const result = await fetchFunction()
      setData(result)
      setLastUpdated(new Date())
    } catch (err) {
      setError(err.message || 'Failed to fetch data')
      console.error('Auto-refresh error:', err)
    } finally {
      setLoading(false)
    }
  }, [fetchFunction])

  useEffect(() => {
    // Initial fetch
    refresh()

    // Set up auto-refresh if enabled
    if (enableAutoRefresh && interval > 0) {
      const intervalId = setInterval(refresh, interval)
      return () => clearInterval(intervalId)
    }
  }, [refresh, interval, enableAutoRefresh])

  return {
    data,
    loading,
    error,
    refresh,
    lastUpdated
  }
}

/**
 * Custom hook for managing online/offline status
 * @returns {boolean} - Online status
 */
export const useOnlineStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine)

  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  return isOnline
}

/**
 * Custom hook for managing local storage with error handling
 * @param {string} key - Local storage key
 * @param {*} initialValue - Initial value
 * @returns {Array} - [value, setValue, removeValue]
 */
export const useLocalStorage = (key, initialValue) => {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key)
      return item ? JSON.parse(item) : initialValue
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error)
      return initialValue
    }
  })

  const setValue = useCallback((value) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value
      setStoredValue(valueToStore)
      window.localStorage.setItem(key, JSON.stringify(valueToStore))
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error)
    }
  }, [key, storedValue])

  const removeValue = useCallback(() => {
    try {
      window.localStorage.removeItem(key)
      setStoredValue(initialValue)
    } catch (error) {
      console.error(`Error removing localStorage key "${key}":`, error)
    }
  }, [key, initialValue])

  return [storedValue, setValue, removeValue]
}

export default useAutoRefresh